import React from "react";
import { Autocomplete, Grid, TextField, Typography } from "@mui/material";
import {
  Edit as EditIcon,
  LocalShipping as LocalShippingIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import Button from "components/atoms/Button";
import { CoverageAreaProps } from "store/modules/Logistics/types.store";
import { DealerUsersProps } from "store/modules/DealerUsers/types.store";
import * as Shared from "../../styles";
import * as Styled from "./styles";
import { useCardDriverAreaSingle } from "./useCardDriverAreaSingle";
import { nonMapAreaDriverAutocompleteSx, selectDriverButtonSx } from "./muiSx";

interface CardDriverAreaSingleProps {
  dealerSelected: string;
  currentCoverageArea: CoverageAreaProps;
  dealerDrivers: DealerUsersProps[];
}

function CardDriverAreaSingle({
  dealerSelected,
  currentCoverageArea,
  dealerDrivers,
}: CardDriverAreaSingleProps) {
  const { t } = useTranslation();
  const {
    addDrive,
    setAddDrive,
    driveSelected,
    setDriveSelected,
    IUsers,
    openDriverSelection,
    handleApply,
    isApplyDisabled,
    selectedDriverWithDealerInfo,
    hasAreaDriverAssigned,
  } = useCardDriverAreaSingle(
    dealerSelected,
    currentCoverageArea,
    dealerDrivers,
  );

  return (
    <Shared.Container $hasAreaDriverAssigned={hasAreaDriverAssigned}>
      <Shared.TopContainer>
        {!hasAreaDriverAssigned && (
          <WarningIcon fontSize="small" color="error" />
        )}
        <Typography variant="body1" fontWeight={600}>
          {t("assignADelivery")}
        </Typography>
      </Shared.TopContainer>

      <Styled.ActionsContainer editMode={false}>
        {!addDrive ? (
          <Styled.ContainerDrive>
            <Styled.ContainerDriverSelected>
              <LocalShippingIcon fontSize="small" color="inherit" />
              <Styled.ContainerText>
                <Typography variant="caption" textAlign="start">
                  {t("deliveryman")}
                </Typography>
                <Typography variant="subtitle2" textAlign="start">
                  {hasAreaDriverAssigned
                    ? (
                        selectedDriverWithDealerInfo?.label ??
                        driveSelected?.label
                      )?.trim() || t("undefined")
                    : t("undefined")}
                </Typography>
              </Styled.ContainerText>
            </Styled.ContainerDriverSelected>

            <Button
              sx={selectDriverButtonSx}
              variant={!driveSelected ? "contained" : "outlined"}
              endIcon={<EditIcon />}
              onClick={openDriverSelection}
            >
              {t("selectDeliveryPerson").toUpperCase()}
            </Button>
          </Styled.ContainerDrive>
        ) : (
          <Styled.ContainerAutoComplete>
            <Autocomplete
              disablePortal
              id="combo-box-demo"
              value={driveSelected}
              options={IUsers ?? []}
              size="small"
              onChange={(_, e) => setDriveSelected(e ?? undefined)}
              renderInput={(params) => (
                <TextField {...params} label={t("selectDriver")} />
              )}
              sx={nonMapAreaDriverAutocompleteSx}
            />

            <Grid>
              <Styled.ContainerButtonsDrive>
                <Button
                  color="inherit"
                  variant="outlined"
                  type="button"
                  onClick={() => setAddDrive(false)}
                >
                  <Typography variant="button" textAlign="start">
                    {t("exit").toUpperCase()}
                  </Typography>
                </Button>

                <Button
                  color="error"
                  variant="contained"
                  type="button"
                  disabled={isApplyDisabled}
                  onClick={handleApply}
                >
                  {t("toApply").toUpperCase()}
                </Button>
              </Styled.ContainerButtonsDrive>
            </Grid>
          </Styled.ContainerAutoComplete>
        )}
      </Styled.ActionsContainer>
    </Shared.Container>
  );
}

export default CardDriverAreaSingle;
